import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  Plus,
  TrendingUp,
  Target,
  Clock,
  ChevronRight,
  Activity,
  Download,
  FileSpreadsheet,
  Receipt,
  Building2,
  FileText
} from 'lucide-react';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell
} from 'recharts';

import NavBar from '../components/NavBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_COLORS = {
  digital_strategy: '#3b82f6',
  technology_infrastructure: '#8b5cf6',
  data_analytics_ai: '#06b6d4',
  process_automation: '#10b981',
  customer_experience: '#f59e0b',
  organization_culture: '#ec4899'
};

const DashboardPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState({ recent_assessments: [], recent_reports: [] });
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/recent`)
      ]);
      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getBenchmarkLabel = (position) => {
    const labels = {
      digital_leader: { tr: 'Dijital Lider', en: 'Digital Leader', color: 'text-emerald-400' },
      above_average: { tr: 'Ortalamanın Üstünde', en: 'Above Average', color: 'text-blue-400' },
      industry_average: { tr: 'Sektör Ortalaması', en: 'Industry Average', color: 'text-yellow-400' },
      below_average: { tr: 'Ortalamanın Altında', en: 'Below Average', color: 'text-orange-400' },
      digitally_lagging: { tr: 'Dijital Geciken', en: 'Digitally Lagging', color: 'text-red-400' }
    };
    return labels[position] || { tr: '-', en: '-', color: 'text-slate-400' };
  };
  
  // Mock radar data for demo
  const radarData = [
    { pillar: language === 'tr' ? 'Dijital Strateji' : 'Digital Strategy', score: 65, fullMark: 100 },
    { pillar: language === 'tr' ? 'Teknoloji' : 'Technology', score: 72, fullMark: 100 },
    { pillar: language === 'tr' ? 'Veri & AI' : 'Data & AI', score: 58, fullMark: 100 },
    { pillar: language === 'tr' ? 'Otomasyon' : 'Automation', score: 45, fullMark: 100 },
    { pillar: language === 'tr' ? 'Müşteri' : 'Customer', score: 68, fullMark: 100 },
    { pillar: language === 'tr' ? 'Kültür' : 'Culture', score: 52, fullMark: 100 },
  ];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">{t('loading')}</div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950" data-testid="dashboard-page">
      <NavBar />
      
      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            {t('welcomeBack')}, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-slate-400">
            {language === 'tr' 
              ? 'Dijital olgunluk değerlendirmelerinizi ve raporlarınızı yönetin.'
              : 'Manage your digital maturity assessments and reports.'}
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:text-white"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/export/assessments/excel`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'degerlendirmeler.xlsx';
                  link.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) { console.error(e); }
              }}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:text-white"
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/export/assessments/csv`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = 'degerlendirmeler.csv';
                  link.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) { console.error(e); }
              }}
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <Link to="/invoices">
              <Button
                variant="outline"
                size="sm"
                className="border-slate-700 text-slate-300 hover:text-white"
                data-testid="invoices-btn"
              >
                <Receipt className="h-4 w-4 mr-1" />
                {language === 'tr' ? 'Faturalar' : 'Invoices'}
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900/50 border-slate-800" data-testid="stat-total-assessments">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">{t('totalAssessments')}</p>
                  <p className="text-3xl font-bold text-white">{stats?.total_assessments || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-500/10 text-blue-400">
                  <Activity size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-800" data-testid="stat-completed">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">{t('completedAssessments')}</p>
                  <p className="text-3xl font-bold text-white">{stats?.completed_assessments || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-400">
                  <Target size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-800" data-testid="stat-avg-gdmii">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">{t('averageScore')}</p>
                  <p className="text-3xl font-bold text-white">{stats?.average_gdmii?.toFixed(1) || '0.0'}</p>
                </div>
                <div className="p-3 rounded-lg bg-purple-500/10 text-purple-400">
                  <TrendingUp size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900/50 border-slate-800" data-testid="stat-companies">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 mb-1">{t('companiesCount')}</p>
                  <p className="text-3xl font-bold text-white">{stats?.companies_count || 0}</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-500/10 text-amber-400">
                  <Building2 size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Radar Chart */}
          <Card className="lg:col-span-2 bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">
                {language === 'tr' ? 'Pillar Performansı' : 'Pillar Performance'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.completed_assessments > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#334155" />
                      <PolarAngleAxis 
                        dataKey="pillar" 
                        tick={{ fill: '#94a3b8', fontSize: 12 }}
                      />
                      <PolarRadiusAxis 
                        angle={30} 
                        domain={[0, 100]} 
                        tick={{ fill: '#64748b', fontSize: 10 }}
                      />
                      <Radar
                        name="Score"
                        dataKey="score"
                        stroke="#3b82f6"
                        fill="#3b82f6"
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center text-slate-500">
                  {language === 'tr' 
                    ? 'Henüz tamamlanmış değerlendirme yok'
                    : 'No completed assessments yet'}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Quick Actions */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">{t('quickActions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Link to="/assessments/new" className="block">
                <Button 
                  className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="quick-new-assessment"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('newAssessment')}
                </Button>
              </Link>
              
              <Link to="/companies/new" className="block">
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="quick-new-company"
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  {t('addCompany')}
                </Button>
              </Link>
              
              <Link to="/reports" className="block">
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="quick-view-reports"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  {t('viewReports')}
                </Button>
              </Link>
              
              {/* Benchmark Position */}
              {stats?.benchmark_position && (
                <div className="mt-6 p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <p className="text-sm text-slate-400 mb-2">
                    {language === 'tr' ? 'Benchmark Pozisyonu' : 'Benchmark Position'}
                  </p>
                  <p className={`text-lg font-semibold ${getBenchmarkLabel(stats.benchmark_position).color}`}>
                    {getBenchmarkLabel(stats.benchmark_position)[language]}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Recent Assessments */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white text-lg">
                {language === 'tr' ? 'Son Değerlendirmeler' : 'Recent Assessments'}
              </CardTitle>
              <Link to="/assessments">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                  {language === 'tr' ? 'Tümünü Gör' : 'View All'}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentActivity.recent_assessments?.length > 0 ? (
                <div className="space-y-3">
                  {recentActivity.recent_assessments.map((assessment) => (
                    <Link 
                      key={assessment.id} 
                      to={`/assessments/${assessment.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          assessment.status === 'completed' ? 'bg-emerald-400' : 'bg-amber-400'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-white">{assessment.company_name}</p>
                          <p className="text-xs text-slate-500">
                            {assessment.status === 'completed' 
                              ? `GDMII: ${assessment.gdmii_score?.toFixed(1)}`
                              : (language === 'tr' ? 'Devam ediyor' : 'In progress')}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-600" />
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-8">
                  {language === 'tr' ? 'Henüz değerlendirme yok' : 'No assessments yet'}
                </p>
              )}
            </CardContent>
          </Card>
          
          {/* Recent Reports */}
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white text-lg">
                {language === 'tr' ? 'Son Raporlar' : 'Recent Reports'}
              </CardTitle>
              <Link to="/reports">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                  {language === 'tr' ? 'Tümünü Gör' : 'View All'}
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentActivity.recent_reports?.length > 0 ? (
                <div className="space-y-3">
                  {recentActivity.recent_reports.map((report) => (
                    <Link 
                      key={report.id} 
                      to={`/reports/${report.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <FileText className="h-4 w-4 text-blue-400" />
                        <div>
                          <p className="text-sm font-medium text-white">{report.company_name}</p>
                          <p className="text-xs text-slate-500">GDMII: {report.gdmii_score?.toFixed(1)}</p>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-600" />
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-8">
                  {language === 'tr' ? 'Henüz rapor yok' : 'No reports yet'}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
